/**
 * ObjectHeader — the top of an object page
 * (docs/features/journey-visual-language/01_proposal.md §4): the lineage
 * breadcrumb; the type and number with exactly two pills (status, then health
 * when there is something to say), and who it is for and its value on the
 * right; then a short line of key facts, each its own item, and under it any
 * groups of facts the object draws together, one line each ("Net £512.00 ·
 * VAT £102.40 · Gross £614.40", `record.fact_groups`). The rail sits in
 * its own card beneath, and the tabs beneath that — except on a page that
 * folds them into this card (the order, round 16c): a disclosure after the
 * facts ("Details ▾") and the card's own sections below them.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Card, CardContent } from "../primitives/card";

export interface Crumb {
  label: string;
  to?: string;
}

export interface KeyFact {
  label: string;
  value: ReactNode;
}

/** Facts drawn together on a line of their own, the label first where there is one. */
export interface KeyFactGroup {
  label: string | null;
  facts: KeyFact[];
}

export function ObjectHeader({
  crumbs,
  title,
  pills,
  subtitle,
  counterpart,
  value,
  valueLabel,
  facts,
  factGroups = [],
  disclosure,
  actions,
  children,
}: {
  crumbs: Crumb[];
  title: string;
  /** Status, then health: two at most. */
  pills: ReactNode;
  /** One compact line under the title, on every tab (a purchase order's *Sent to supplier*). */
  subtitle?: ReactNode;
  /** Who it is for: the customer, or the supplier on a purchase order. */
  counterpart?: ReactNode;
  /** Price, or cost on a purchase order. */
  value?: ReactNode;
  /** What the value is, after it: "order total, ex VAT", "cost, ex VAT". */
  valueLabel?: string;
  facts: KeyFact[];
  /** Lines of facts under the fact line, each fact separated by " · ". */
  factGroups?: KeyFactGroup[];
  /**
   * The object's own actions ("Add extra"), at the right of the key facts:
   * at most two buttons, the rest in a ⋯ menu.
   */
  actions?: ReactNode;
  /** A toggle after the facts that opens more of the object below them. */
  disclosure?: ReactNode;
  /** The card's own sections under the facts (each divided from the last). */
  children?: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1">
          {crumbs.map((crumb, i) => (
            <li
              key={`${crumb.label}-${i}`}
              className="flex min-w-0 items-center gap-1 break-words"
            >
              {i > 0 && <span aria-hidden="true">›</span>}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className="hover:text-foreground hover:underline"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page" className="text-foreground">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <Card>
        <CardContent className="space-y-3 py-5 max-sm:px-4">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="min-w-0 break-words text-2xl font-semibold max-sm:text-xl">
                {title}
              </h1>
              {pills}
            </div>
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-4">
              {counterpart && (
                <span className="min-w-0 break-words font-medium text-foreground">
                  {counterpart}
                </span>
              )}
              {value && (
                <span className="text-xl font-semibold tabular-nums">
                  {value}
                  {valueLabel && (
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      {valueLabel}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
          {subtitle}
          {/* A header with no line of facts (the tabbed record draws its key facts as its own grid) leaves no gap. */}
          {(facts.length > 0 || disclosure || actions) && (
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm max-sm:w-full">
                <dl className="flex flex-wrap items-baseline gap-y-1 max-sm:grid max-sm:w-full max-sm:grid-cols-2 max-sm:gap-x-4 max-sm:gap-y-2">
                  {facts.map((fact, i) => (
                    <div
                      key={fact.label}
                      // The divider trails each item, so a wrapped line starts clean.
                      className={
                        i < facts.length - 1
                          ? "mr-4 min-w-0 border-r border-border pr-4 max-sm:mr-0 max-sm:border-r-0 max-sm:pr-0"
                          : "min-w-0"
                      }
                    >
                      <dt className="inline text-muted-foreground max-sm:block max-sm:text-xs">
                        {fact.label}{" "}
                      </dt>
                      <dd className="inline break-words font-medium text-foreground max-sm:block">
                        {fact.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                {disclosure}
              </div>
              {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
            </div>
          )}
          {factGroups.map((group, g) => (
            <div
              key={group.label ?? `group-${g}`}
              className="flex flex-wrap items-baseline gap-y-1 text-sm"
            >
              {group.label && (
                <span className="mr-3 text-muted-foreground">
                  {group.label}
                </span>
              )}
              <dl className="flex flex-wrap items-baseline gap-y-1">
                {group.facts.map((fact, i) => (
                  <div key={fact.label} className="min-w-0">
                    <dt className="inline text-muted-foreground">
                      {fact.label}{" "}
                    </dt>
                    <dd className="inline break-words font-medium tabular-nums text-foreground">
                      {fact.value}
                    </dd>
                    {i < group.facts.length - 1 && (
                      <span
                        aria-hidden="true"
                        className="mx-2 text-muted-foreground"
                      >
                        ·
                      </span>
                    )}
                  </div>
                ))}
              </dl>
            </div>
          ))}
          {children}
        </CardContent>
      </Card>
    </div>
  );
}
